import { Box } from '@mui/material';
import { useFormikContext } from 'formik';
import React, { memo, useCallback, useMemo } from 'react';
import { ColorResult, RGBColor } from 'react-color';
import { useSelector } from 'react-redux';

import {
  getInitialTemplateBuilderValues,
  GLOBAL_HEADER_MEDIA,
  GLOBAL_HERO_SECTION_BG_COLOR,
  GLOBAL_HERO_SECTION_BG_TYPE,
  GLOBAL_HERO_SECTION_FIT,
  GLOBAL_HERO_SECTION_IMAGE,
} from '../../../../../constants/templates';
import { useTemplateMediaUpload } from '../../../../../hooks/useTemplateImageUpload';
import { selectCurrentOrganizationLogoUrl } from '../../../../../store/selectors/organizations';
import { selectDefaultExperienceBlueprint } from '../../../../../store/selectors/shell';
import { ImageFitEnum } from '../../../../../types/shell';
import { IClaimLinkBuilderValues, UploadControlModeEnum } from '../../../../../types/templates';
import { resolveHeroBackgroundType } from '../../../../../utils/templates';
import { HeaderMediaUploader, HeroBackgroundControl } from '../../TemplateUploadControls';

export const HeroStep: React.FC = memo(() => {
  const { values, setFieldValue } = useFormikContext<IClaimLinkBuilderValues>();
  const blueprint = useSelector(selectDefaultExperienceBlueprint);
  const organizationLogoUrl = useSelector(selectCurrentOrganizationLogoUrl);

  const {
    globalHeaderMediaUrl,
    globalHeroSectionBackgroundColor,
    globalHeroSectionImageUrl,
    globalHeroSectionImageFit,
    globalHeroSectionBackgroundType,
  } = values;

  const heroBackgroundType = useMemo(
    () => resolveHeroBackgroundType(globalHeroSectionBackgroundType, globalHeroSectionImageUrl),
    [globalHeroSectionBackgroundType, globalHeroSectionImageUrl],
  );

  const blueprintDefaults = useMemo(
    () => getInitialTemplateBuilderValues(blueprint, organizationLogoUrl),
    [blueprint, organizationLogoUrl],
  );

  const handleMediaSuccess = useCallback(
    (fieldName: keyof IClaimLinkBuilderValues, value: string | null) => {
      setFieldValue(fieldName, value || '');
    },
    [setFieldValue],
  );

  const { handleMediaUpload, handleMediaRemove } = useTemplateMediaUpload({ onSuccess: handleMediaSuccess });

  const handleFieldChange = useCallback(
    <K extends keyof IClaimLinkBuilderValues>(fieldName: K) =>
      (value: IClaimLinkBuilderValues[K]) => {
        setFieldValue(fieldName, value);
      },
    [setFieldValue],
  );

  const handleColorChange = useCallback(
    (fieldName: keyof IClaimLinkBuilderValues) => (color: ColorResult) => {
      setFieldValue(fieldName, color.rgb);
    },
    [setFieldValue],
  );

  const handleResetColor = useCallback(
    (fieldName: keyof IClaimLinkBuilderValues) => () => {
      setFieldValue(fieldName, blueprintDefaults[fieldName]);
    },
    [setFieldValue, blueprintDefaults],
  );

  const handleResetHeaderMedia = useCallback(() => {
    handleFieldChange(GLOBAL_HEADER_MEDIA)(blueprintDefaults.globalHeaderMediaUrl);
  }, [handleFieldChange, blueprintDefaults.globalHeaderMediaUrl]);

  const handleImageFitChange = useCallback(
    (fit: ImageFitEnum) => {
      handleFieldChange(GLOBAL_HERO_SECTION_FIT)(fit);
    },
    [handleFieldChange],
  );

  const handleBackgroundImageRemove = useCallback(() => {
    const removeHandler = handleMediaRemove(GLOBAL_HERO_SECTION_IMAGE);
    removeHandler();
    handleFieldChange(GLOBAL_HERO_SECTION_FIT)(ImageFitEnum.Cover);
  }, [handleMediaRemove, handleFieldChange]);

  const handleBackgroundColorChange = useCallback(
    (color: RGBColor) => {
      const colorHandler = handleColorChange(GLOBAL_HERO_SECTION_BG_COLOR);
      colorHandler({ rgb: color } as ColorResult);
    },
    [handleColorChange],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <HeaderMediaUploader
        mode={UploadControlModeEnum.Global}
        globalValue={globalHeaderMediaUrl}
        defaultValue={blueprintDefaults.globalHeaderMediaUrl}
        onDrop={handleMediaUpload(GLOBAL_HEADER_MEDIA)}
        onRemove={handleMediaRemove(GLOBAL_HEADER_MEDIA)}
        onReset={handleResetHeaderMedia}
        dataAction="header_media"
      />
      <Box sx={{ mt: 3 }}>
        <HeroBackgroundControl
          mode={UploadControlModeEnum.Global}
          backgroundType={heroBackgroundType}
          onBackgroundTypeChange={handleFieldChange(GLOBAL_HERO_SECTION_BG_TYPE)}
          globalColor={globalHeroSectionBackgroundColor}
          defaultColor={blueprintDefaults.globalHeroSectionBackgroundColor}
          globalImageUrl={globalHeroSectionImageUrl}
          globalImageFit={globalHeroSectionImageFit}
          onColorChange={handleBackgroundColorChange}
          onColorReset={handleResetColor(GLOBAL_HERO_SECTION_BG_COLOR)}
          onImageDrop={handleMediaUpload(GLOBAL_HERO_SECTION_IMAGE)}
          onImageRemove={handleBackgroundImageRemove}
          onImageFitChange={handleImageFitChange}
          dataAction="background"
        />
      </Box>
    </Box>
  );
});
